// src/components/home/awareness/ProfileBannerAwarenessCampaign.js

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import ProfileBannerDrawer from '../../ProfileBannerDrawer';

import {
  getCatalogue,
} from '../../../services/profileBannerApi';

import {
  clearAwarenessCooldown,
  hasAwarenessCooldown,
  setAwarenessCooldown,
} from './awarenessStorage';

const CAMPAIGN_KEY = 'profile_banner';

const SEVEN_DAYS_MS =
  7 * 24 * 60 * 60 * 1000;

const DEFAULT_OPEN_DELAY_MS = 900;

const getBannerId = (banner) => {
  return (
    banner?._id ||
    banner?.id ||
    banner?.bannerId ||
    ''
  );
};

const getCatalogueItems = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.banners)) {
    return data.banners;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  if (Array.isArray(data?.catalogue)) {
    return data.catalogue;
  }

  return [];
};

const getTrialUsed = (data) => {
  return Boolean(
    data?.trial?.used ??
      data?.trialUsed ??
      data?.profileBannerTrialUsed ??
      data?.hasUsedTrial
  );
};

function ProfileBannerAwarenessCampaign({
  user,
  onUserUpdate,

  // The manager decides when Banner may check and open.
  enabled = false,

  // Reports whether Banner is currently eligible.
  onEligibilityResolved,

  // Reports that the drawer has actually opened.
  onOpened,

  // Reports dismissal, trial or purchase.
  onFinished,

  openDelayMs = DEFAULT_OPEN_DELAY_MS,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [banners, setBanners] = useState([]);
  const [selectedBanner, setSelectedBanner] =
    useState(null);
  const [trialUsed, setTrialUsed] = useState(false);

  /*
   * Prevent duplicate eligibility requests during one manager cycle.
   */
  const checkedRef = useRef(false);

  /*
   * Distinguishes trial/purchase success from a normal close.
   */
  const completedRef = useRef(false);

  const username = String(user?.username || '')
    .trim()
    .toLowerCase();

  const accountType = String(
    user?.accountType || 'primary'
  ).toLowerCase();

  const resolveEligibility = useCallback(
    (eligible) => {
      onEligibilityResolved?.({
        campaignKey: CAMPAIGN_KEY,
        eligible: Boolean(eligible),
      });
    },
    [onEligibilityResolved]
  );

  /**
   * Reset all Banner state when the logged-in identity changes.
   */
  useEffect(() => {
    setIsOpen(false);
    setBanners([]);
    setSelectedBanner(null);
    setTrialUsed(false);

    checkedRef.current = false;
    completedRef.current = false;
  }, [username]);

  /**
   * Close safely when the manager disables this campaign.
   *
   * This can happen when another Home drawer opens or when the
   * manager changes stages. It does not count as a dismissal.
   */
  useEffect(() => {
    if (enabled || !isOpen) {
      return;
    }

    setIsOpen(false);
    setSelectedBanner(null);
  }, [enabled, isOpen]);

  /**
   * Prepare Banner for a later manager cycle.
   *
   * This fixes the same issue that affected Reward. Once Banner is
   * disabled and closed, a later manager cycle must be allowed to
   * check its eligibility again.
   */
  useEffect(() => {
    if (enabled || isOpen) {
      return;
    }

    checkedRef.current = false;
    completedRef.current = false;
  }, [enabled, isOpen]);

  /**
   * Check Banner eligibility only when enabled by the manager.
   */
  useEffect(() => {
    if (!username) {
      return undefined;
    }

    /*
     * App identities cannot use personal profile banners.
     */
    if (accountType === 'app') {
      setIsOpen(false);
      resolveEligibility(false);

      return undefined;
    }

    if (!enabled) {
      return undefined;
    }

    /*
     * Avoid repeated requests while Banner remains enabled during
     * the current manager cycle.
     */
    if (checkedRef.current) {
      return undefined;
    }

    checkedRef.current = true;

    /*
     * The user dismissed Banner during the campaign cooldown.
     *
     * Report false immediately so the manager can finish the
     * current priority sequence.
     */
    if (
      hasAwarenessCooldown(
        CAMPAIGN_KEY,
        username
      )
    ) {
      setIsOpen(false);
      resolveEligibility(false);

      return undefined;
    }

    let cancelled = false;
    let timerId = null;

    const checkEligibility = async () => {
      try {
        const data = await getCatalogue();

        if (cancelled) {
          return;
        }

        const items = getCatalogueItems(data);
        const hasUsedTrial = getTrialUsed(data);

        const ownsAnyBanner = items.some(
          (banner) => banner?.owned === true
        );

        const firstEligibleBanner = items.find(
          (banner) =>
            banner?.canStartTrial === true &&
            Boolean(getBannerId(banner))
        );

        const eligible =
          !hasUsedTrial &&
          !ownsAnyBanner &&
          Boolean(firstEligibleBanner);

        setBanners(items);
        setTrialUsed(hasUsedTrial);

        /*
         * Tell the manager whether Banner can open.
         */
        resolveEligibility(eligible);

        if (!eligible) {
          setIsOpen(false);
          setSelectedBanner(null);

          /*
           * The backend now says the campaign is permanently
           * ineligible, so any old dismissal cooldown is unnecessary.
           */
          clearAwarenessCooldown(
            CAMPAIGN_KEY,
            username
          );

          return;
        }

        setSelectedBanner(firstEligibleBanner);

        completedRef.current = false;
        setIsOpen(true);

        onOpened?.({
          campaignKey: CAMPAIGN_KEY,
        });
      } catch (error) {
        const code = String(
          error?.response?.data?.code || ''
        ).toUpperCase();

        const expectedIneligibleError =
          code === 'APP_ACCOUNT_NOT_ALLOWED' ||
          code === 'ACCOUNT_SUSPENDED' ||
          code === 'USER_NOT_FOUND';

        if (!expectedIneligibleError) {
          console.error(
            'Failed to check profile banner awareness:',
            error
          );
        }

        if (!cancelled) {
          setIsOpen(false);
          setSelectedBanner(null);

          /*
           * Do not leave the manager waiting forever if the Banner
           * request fails.
           */
          resolveEligibility(false);
        }
      }
    };

    timerId = window.setTimeout(() => {
      void checkEligibility();
    }, Math.max(0, Number(openDelayMs) || 0));

    return () => {
      cancelled = true;

      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [
    username,
    accountType,
    enabled,
    openDelayMs,
    onOpened,
    resolveEligibility,
  ]);

  /**
   * Called when the user closes the drawer with:
   *
   * - the close button
   * - the backdrop
   *
   * ProfileBannerDrawer may also call onClose after a successful
   * trial or purchase. completedRef prevents that success from being
   * incorrectly recorded as a dismissal.
   */
  const handleClose = useCallback(() => {
    const completedSuccessfully =
      completedRef.current === true;

    if (!completedSuccessfully && username) {
      setAwarenessCooldown({
        campaignKey: CAMPAIGN_KEY,
        username,
        durationMs: SEVEN_DAYS_MS,
      });

      onFinished?.({
        campaignKey: CAMPAIGN_KEY,
        outcome: 'dismissed',
      });
    }

    setIsOpen(false);
    setSelectedBanner(null);

    completedRef.current = false;
  }, [
    onFinished,
    username,
  ]);

  /**
   * Trial and purchase both count as successful Banner adoption.
   */
  const handleBannerStateChange = useCallback(
    async ({
      equippedBanner,
      action,
      response,
    } = {}) => {
      const successfulAdoption =
        action === 'trial' ||
        action === 'purchase';

      if (!successfulAdoption) {
        return;
      }

      /*
       * Set this before the drawer calls onClose.
       */
      completedRef.current = true;

      clearAwarenessCooldown(
        CAMPAIGN_KEY,
        username
      );

      if (typeof onUserUpdate === 'function') {
        const nextUser = {
          ...(user || {}),

          equippedProfileBanner:
            equippedBanner ||
            user?.equippedProfileBanner ||
            null,
        };

        if (action === 'trial') {
          nextUser.profileBannerTrialUsed = true;

          nextUser.profileBannerTrialBanner =
            getBannerId(equippedBanner) ||
            getBannerId(selectedBanner) ||
            user?.profileBannerTrialBanner ||
            null;
        }

        await Promise.resolve(
          onUserUpdate(nextUser)
        );
      }

      /*
       * Remove the cached self-profile so Profile.js fetches the
       * newly equipped banner.
       */
      try {
        if (username) {
          const ownProfileCacheKey =
            `viewedUserProfile:${username}:${username}`;

          localStorage.removeItem(
            ownProfileCacheKey
          );
        }
      } catch (_) {}

      setIsOpen(false);

      onFinished?.({
        campaignKey: CAMPAIGN_KEY,
        outcome: 'completed',
        action,
        response: response || null,
      });

      /*
       * Do not reset completedRef here.
       *
       * The shared drawer may call onClose after this callback.
       * handleClose must still see completedRef === true so it does
       * not report a second dismissal.
       */
    },
    [
      onFinished,
      onUserUpdate,
      selectedBanner,
      user,
      username,
    ]
  );

  return (
    <ProfileBannerDrawer
      isOpen={
        isOpen &&
        Boolean(selectedBanner)
      }
      banner={selectedBanner}
      banners={banners}
      user={user}
      trialUsed={trialUsed}
      onSelectBanner={setSelectedBanner}
      onClose={handleClose}
      onBannerStateChange={
        handleBannerStateChange
      }
    />
  );
}

export default ProfileBannerAwarenessCampaign;
